import { useMemo, useEffect, useState, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { Formik } from 'formik';
import Grid from '@mui/material/Grid';
import CircularProgress from '@mui/material/CircularProgress';
import MKBox from 'components/MaterialKit/MKBox';
import MKTypography from 'components/MaterialKit/MKTypography';
import InputField, { INPUT_TYPE_SELECT } from 'components/InputField';
import Button from 'components/Button';
import { getLocaleMap } from 'utils/locales';
import { useNavigate } from 'react-router-dom';
import Select from 'components/Select';
import mapFp from 'lodash/fp/map';
import map from 'lodash/map';
import clone from 'lodash/clone';
import cloneDeep from 'lodash/cloneDeep';
import words from 'lodash/words';
import phoneOptionsTemplate from 'components/Select/phone_options.json';
import { splitPhoneNumber } from 'utils/phone';
import { getIWatchAccounts } from 'api/iwatch';
import { parsePhoneNumberFromString, ParseError } from 'libphonenumber-js/max';

const mapValue = mapFp('value');

const EditContactForm = ({ contact, onSave, countries,
  editButtonColor,
  editFontColor,
  saveButtonColor,
  saveButtonFontColor,
  backButtonColor,
  backButtonFontColor,
  countryOptions,
  ...props
}) => {
  const [lm, setLm] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    getLocaleMap([
      'errors_required',
      'save_button',
    ])
      .then((response) => {
        setLm(response);
      });
  }, []);

  const getFormValidation = (values) => {
    const { author } = values;
    const errors = {};
    if (!author) {
      errors.author = lm.errors_required;
    }
    return errors;
  };

  const initialValues = useMemo(() => {
    const contactCountries = contact?.contact_countries || [];
    const contactCountryIds = map(contactCountries, ['country', 'country_id']);
    return {
      author: contact?.author || '',
      company_name: contact?.company_name || '',
      is_blacklisted: contact?.is_blacklisted || false,
      account: contact?.account || '',
      phone: contact?.phone || '',
      contact_countries: contactCountries,
      initial_countries: contactCountryIds,
      selected_countries: clone(contactCountryIds),
    };
  }, [contact]);

  const countriesOptions = useMemo(() => countries.map((country) => ({
    label: country.name,
    value: country.country_id,
  })), [countries]);

  const [selectedCountries, setSelectedCountrires] = useState([]);
  const [phoneOptions, setPhoneOptions] = useState(cloneDeep(phoneOptionsTemplate));
  const [blacklistOptions, setBlacklistOptions] = useState([
    { label: 'Yes', value: true },
    { label: 'No', value: false }
  ]);
  const [sourceAccountOptions, setSourceAccountOptions] = useState([
    { label: '(none)', value: null },
  ]);
  useEffect(() => {
    getIWatchAccounts({ active: true, $orderby: 'sequence' }).then(({ data }) => {
      const options = map(data, iWatchAccount => {
        const accountName = iWatchAccount.name;
        const phone = iWatchAccount.phone;
        try {
          const phoneNumber = parsePhoneNumberFromString('+' + phone);
          const formattedPhoneNumber = phoneNumber && phoneNumber.formatInternational() || phone;
          return {
            label: `${iWatchAccount.key} (${accountName}) (${formattedPhoneNumber})`,
            value: accountName,
          };
        } catch (e) {
          if (e instanceof ParseError) {
            // Not a phone number, non-existent country, etc.
            console.log(e.message);
          } else {
            throw e;
          }
        }
      });
      options.unshift({ label: '(none)', value: null });
      setSourceAccountOptions(options);
    });
  }, []);

  const handleChangeCountry = useCallback((values, setFieldValue) => {
    const added = values.filter((item) => {
      return selectedCountries.indexOf(item) === -1;
    });
    const removed = selectedCountries.filter((item) => {
      return values.indexOf(item) === -1;
    });

    if (added.includes('all')) {
      const ids = mapValue(countriesOptions);
      setFieldValue('selected_countries', ['all', ...ids]);
      setSelectedCountrires(['all', ...ids]);
    } else if (removed.includes('all')) {
      setFieldValue('selected_countries', []);
      setSelectedCountrires([]);
    } else if (removed.length > 0) {
      const indexOfAll = values.indexOf('all');
      if (indexOfAll > -1) { // only splice array when item is found
        values.splice(indexOfAll, 1);
      }
      setFieldValue('selected_countries', values);
      setSelectedCountrires(clone(values));
    } else {
      let newValues;
      const ids = values.filter(value => value !== 'all');
      if (ids.length === countries.length) {
        newValues = ['all', ...ids];
      } else {
        newValues = values;
      }
      setFieldValue('selected_countries', newValues);
      setSelectedCountrires(clone(newValues));
    }
  }, [countries.length, countriesOptions, selectedCountries]);

  const formikRef = useRef();
  useEffect(() => {
    if (formikRef.current && initialValues.contact_countries.length > 0 && countries.length > 0) {
      if ((initialValues.contact_countries.length === countries.length)) {
        formikRef.current.setFieldValue('selected_countries', ['all', ...initialValues.initial_countries]);
        setSelectedCountrires(['all', ...initialValues.initial_countries]);
      }
    }
  }, [countries, initialValues]);

  return (
    <Formik
      innerRef={formikRef}
      onSubmit={onSave}
      initialValues={initialValues}
      validate={getFormValidation}
      enableReinitialize
      {...props}
    >
      {({ handleChange, handleBlur, handleSubmit, setFieldValue, errors, values, touched, isSubmitting, dirty }) => {
        const { phone } = values;
        const [ phoneCountry, phoneRemaining ] = splitPhoneNumber(phone);
        return (
          <MKBox component="form" role="form" onSubmit={handleSubmit}>
            <Grid container spacing={1}>
              <Grid item xs={12} mt={2}>
                <MKBox display="flex" width="100%" justifyContent="space-between">
                  <Button
                    color={backButtonColor || 'info'}
                    disabled={isSubmitting}
                    style={{
                      border: backButtonColor === 'white' && '1px solid black',
                      width: '10vw',
                    }}
                    onClick={() => { navigate(-1); }}
                    sx={{
                      color: backButtonFontColor,
                    }}
                  >
                    Back
                  </Button>
                  <Button
                    type="submit"
                    color={saveButtonColor}
                    disabled={isSubmitting || !dirty}
                    style={{
                      width: '10vw',
                    }}
                    sx={{
                      color: saveButtonFontColor,
                    }}
                  >
                    {isSubmitting ? (
                      <CircularProgress color="white" size={24} sx={{ position: 'absolute' }} />
                    ) : lm.save_button}
                  </Button>
                </MKBox>
                <MKTypography variant="caption" color="error">
                  {errors.form}
                  &nbsp;
                </MKTypography>
              </Grid>
              <Grid container display="flex" flexDirection="row" alignItems="center">
                <Grid item xs={4}>
                  <MKTypography color="black">
                    Contact Name
                  </MKTypography>
                </Grid>
                <Grid item xs={8}>
                  <InputField
                    name="author"
                    label=""
                    value={values.author}
                    handleChange={handleChange}
                    handleBlur={handleBlur}
                    error={touched.author && !!errors.author}
                  />
                </Grid>
              </Grid>
              <MKTypography sx={{ ml: 1, mt: 0.5 }} variant="caption" color="error">
                {(touched.author && !!errors.author) ? errors.author : '\xa0'}
              </MKTypography>
              <Grid container display="flex" flexDirection="row" alignItems="center">
                <Grid item xs={4}>
                  <MKTypography color="black">
                    Company Name
                  </MKTypography>
                </Grid>
                <Grid item xs={8}>
                  <InputField
                    name="company_name"
                    label=""
                    value={values.company_name}
                    handleChange={handleChange}
                    handleBlur={handleBlur}
                    error={touched.company_name && !!errors.company_name}
                  />
                </Grid>
              </Grid>
              <MKTypography sx={{ ml: 1, mt: 0.5 }} variant="caption" color="error">
                {(touched.company_name && !!errors.company_name) ? errors.company_name : '\xa0'}
              </MKTypography>
              <Grid container display="flex" flexDirection="row" alignItems="center">
                <Grid item xs={4}>
                  <MKTypography color="black">
                    Is blacklisted
                  </MKTypography>
                </Grid>
                <Grid item xs={8}>
                  <Select
                    name="is_blacklisted"
                    options={blacklistOptions}
                    value={values.is_blacklisted}
                    onChange={(newValue) => {
                      setFieldValue('is_blacklisted', newValue);
                    }}
                    handleBlur={handleBlur}
                    error={touched.is_blacklisted && !!errors.is_blacklisted}
                  />
                </Grid>
              </Grid>
              <MKTypography sx={{ ml: 1, mt: 0.5 }} variant="caption" color="error">
                {(touched.is_blacklisted && !!errors.is_blacklisted) ? errors.is_blacklisted : '\xa0'}
              </MKTypography>
              <Grid container display="flex" flexDirection="row" alignItems="center">
                <Grid item xs={4}>
                  <MKTypography color="black">
                    Source Feeder Account
                  </MKTypography>
                </Grid>
                <Grid item xs={8}>
                  <Select
                    name="account"
                    options={sourceAccountOptions}
                    value={values.account}
                    onChange={(newValue) => {
                      setFieldValue('account', newValue);
                    }}
                    handleBlur={handleBlur}
                    error={touched.account && !!errors.account}
                  />
                </Grid>
              </Grid>
              <MKTypography sx={{ ml: 1, mt: 0.5 }} variant="caption" color="error">
                {(touched.account && !!errors.account) ? errors.account : '\xa0'}
              </MKTypography>
              <Grid container display="flex" flexDirection="row" alignItems="center">
                <Grid item xs={4}>
                  <MKTypography color="black">
                    Phone country (Whatsapp)
                  </MKTypography>
                </Grid>
                <Grid item xs={8}>
                  <Select
                    name="phone_country"
                    options={phoneOptions}
                    value={phoneCountry}
                    onChange={(newValue) => {
                      setFieldValue('phone', `${newValue}${phoneRemaining}`);
                    }}
                    handleBlur={handleBlur}
                    error={touched.value && !!errors.value}
                  />
                </Grid>
              </Grid>
              {/* <Grid container display="flex" flexDirection="row" alignItems="center"> */}
                {/*}
                <Grid item xs={4}>
                  <MKTypography color="black">
                    Country
                  </MKTypography>
                </Grid>
                */}
                {/*
                <Grid item xs={8}>
                  <InputField
                    name="country"
                    label=""
                    type={INPUT_TYPE_SELECT}
                    value={values.country}
                    options={countryOptions}
                    setFieldValue={setFieldValue}
                    invalid={touched.country && !!errors.country}
                  />
                </Grid>
                */}
                {/*
                <Grid container item flexDirection="column" xs={8}>
                  <Select
                    multiple
                    label="Countries"
                    value={values.selected_countries}
                    onChange={(v) => handleChangeCountry(v, setFieldValue)}
                    options={[{ label: 'All', value: 'all' }, ...countriesOptions]}
                  />
                </Grid>
                */}
              {/* </Grid> */}
              <Grid container display="flex" flexDirection="row" alignItems="center">
                <Grid item xs={4}>
                  <MKTypography color="black">
                    Phone (Whatsapp)
                  </MKTypography>
                </Grid>
                <Grid item xs={8}>
                  <InputField
                    name="phone"
                    label=""
                    value={phone}
                    handleChange={handleChange}
                    handleBlur={handleBlur}
                    error={touched.phone && !!errors.phone}
                  />
                </Grid>
              </Grid>
              <MKTypography sx={{ ml: 1, mt: 0.5 }} variant="caption" color="error">
                {(touched.phone && !!errors.phone) ? errors.phone : '\xa0'}
              </MKTypography>
            </Grid>
          </MKBox>
        );
      }}
    </Formik>
  );
};

EditContactForm.propTypes = {
  contact: PropTypes.object,
  onSave: PropTypes.func,
  countries: PropTypes.array,
  editButtonColor: PropTypes.string,
  editFontColor: PropTypes.string,
  saveButtonColor: PropTypes.string,
  saveButtonFontColor: PropTypes.string,
  backButtonColor: PropTypes.string,
  backButtonFontColor: PropTypes.string,
  countryOptions: PropTypes.array,
};

EditContactForm.defaultProps = {
  contact: {},
  onSave: () => {},
};

export default EditContactForm;
